import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Building2, Save, Upload, Image, Clock, Send, FileText, Trash2, FileSignature } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [sendPriceListOpen, setSendPriceListOpen] = useState(false);
  const [showHistoryOpen, setShowHistoryOpen] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [priceListHistory, setPriceListHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [pdfDeleted, setPdfDeleted] = useState(false); // FORCE SHOW UPLOAD INPUT
  
  const [formData, setFormData] = useState({
    company_name: "",
    website: "",
    address: "",
    postal_code: "",
    city: "",
    vat_number: "",
    email: "",
    phone: "",
    bank_name: "",
    bank_account: "",
    iban: "",
    swift_bic: "",
    usdc_wallet_address: "",
    general_conditions_pdf: null,
    price_list_pdf: null,
    timezone: "Europe/Brussels",
    use_utc: false,
    // Invoice PDF Settings
    invoice_company_name: "",
    invoice_company_subtitle: "",
    invoice_address: "",
    invoice_city: "",
    invoice_vat: "",
    invoice_email: "",
    invoice_website: "",
    invoice_phone: "",
    invoice_dispatch_label: "",
    invoice_footer_line1: "",
    invoice_footer_line2: "",
    invoice_footer_line3: ""
  });

  useEffect(() => {
    fetchSettings();
    fetchAgencies();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      const response = await axios.get(`${API}/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const pdfPath = response.data.price_list_pdf || null;
      
      setFormData({
        company_name: response.data.company_name || "",
        website: response.data.website || "",
        address: response.data.address || "",
        postal_code: response.data.postal_code || "",
        city: response.data.city || "",
        vat_number: response.data.vat_number || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        bank_name: response.data.bank_name || "",
        bank_account: response.data.bank_account || "",
        iban: response.data.iban || "",
        swift_bic: response.data.swift_bic || "",
        usdc_wallet_address: response.data.usdc_wallet_address || "",
        general_conditions_pdf: response.data.general_conditions_pdf || null,
        price_list_pdf: pdfPath,
        timezone: response.data.timezone || "Europe/Brussels",
        use_utc: response.data.use_utc || false,
        // Invoice PDF Settings
        invoice_company_name: response.data.invoice_company_name || "",
        invoice_company_subtitle: response.data.invoice_company_subtitle || "",
        invoice_address: response.data.invoice_address || "",
        invoice_city: response.data.invoice_city || "",
        invoice_vat: response.data.invoice_vat || "",
        invoice_email: response.data.invoice_email || "",
        invoice_website: response.data.invoice_website || "",
        invoice_phone: response.data.invoice_phone || "",
        invoice_dispatch_label: response.data.invoice_dispatch_label || "",
        invoice_footer_line1: response.data.invoice_footer_line1 || "",
        invoice_footer_line2: response.data.invoice_footer_line2 || "",
        invoice_footer_line3: response.data.invoice_footer_line3 || ""
      });
      
      setLogoUrl(response.data.logo_url ? `${BACKEND_URL}${response.data.logo_url}` : null);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        toast.error("Failed to fetch company settings");
      }
    }
    setLoading(false);
  };

  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error("Failed to fetch agencies");
    }
  };

  const fetchPriceListHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/company-settings/price-list-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPriceListHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch price list history");
    }
  };

  const handleClearPriceListHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all price list send history? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/company-settings/price-list-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPriceListHistory([]);
      toast.success("Price list history cleared successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear history");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/company-settings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Company settings updated successfully");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/company-settings/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setLogoUrl(`${BACKEND_URL}${response.data.logo_url}`);
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload logo");
    }
    setUploading(false);
  };

  const handleGeneralConditionsPDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error("Please upload a PDF file");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/company-settings/general-conditions-pdf`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData({ ...formData, general_conditions_pdf: response.data.pdf_path });
      toast.success("General conditions PDF uploaded successfully");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload PDF");
    }
    setUploading(false);
  };

  const handleDeleteGeneralConditionsPDF = async () => {
    if (!window.confirm('Are you sure you want to delete the general conditions PDF?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/company-settings/general-conditions-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({ ...formData, general_conditions_pdf: null });
      toast.success("General conditions PDF deleted");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to delete PDF");
    }
  };

  const handlePriceListPDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error("Please upload a PDF file");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/company-settings/price-list-pdf`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, price_list_pdf: response.data.pdf_path }));
      setPdfDeleted(false); // RESET FLAG - SHOW BUTTONS AGAIN
      
      toast.success("Price list PDF uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload PDF");
      console.error("Upload error:", error);
    }
    setUploading(false);
  };

  const handleDeletePriceListPDF = async () => {
    if (!window.confirm('Are you sure you want to delete the price list PDF?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/company-settings/price-list-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // SET FLAG TO FORCE SHOW UPLOAD INPUT
      setPdfDeleted(true);
      
      toast.success("Price list PDF deleted - upload a new one below");
    } catch (error) {
      toast.error("Failed to delete PDF: " + (error.response?.data?.detail || error.message));
      console.error("Delete error:", error);
    }
  };

  const handleSendPriceList = () => {
    if (!formData.price_list_pdf) {
      toast.error("Please upload a price list PDF first");
      return;
    }
    setSelectedAgencies([]);
    setSendPriceListOpen(true);
  };

  const handleShowHistory = () => {
    fetchPriceListHistory();
    setShowHistoryOpen(true);
  };

  const handleToggleAgency = (agencyId) => {
    setSelectedAgencies(prev => {
      if (prev.includes(agencyId)) {
        return prev.filter(id => id !== agencyId);
      } else {
        return [...prev, agencyId];
      }
    });
  };

  const handleSendPriceListToAgencies = async () => {
    if (selectedAgencies.length === 0) {
      toast.error("Please select at least one agency");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/company-settings/send-price-list`, {
        agency_ids: selectedAgencies
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Price list sent to ${selectedAgencies.length} ${selectedAgencies.length === 1 ? 'agency' : 'agencies'}`);
      setSendPriceListOpen(false);
      setSelectedAgencies([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send price list");
    }
    setSending(false);
  };

  if (loading) {
    return <div style={{ color: '#B0B0B0' }}>Loading...</div>;
  }

  return (
    <div data-testid="company-settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Company Settings</h1>
        <p style={{ color: '#B0B0B0' }}>Manage your company information and bank details</p>
      </div>

      <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)', backdropFilter: 'blur(12px)' }}>
        <CardHeader>
          <CardTitle className="flex items-center" style={{ color: '#FFD700' }}>
            <Building2 className="mr-2 h-5 w-5" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Logo Section */}
            <div className="mb-6 p-4 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#FFD700' }}>Company Logo</h3>
              <div className="flex items-center gap-4">
                {logoUrl && (
                  <div className="w-24 h-24 rounded border flex items-center justify-center" style={{ background: '#FFFFFF', borderColor: '#4A5A8A' }}>
                    <img src={logoUrl} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <Label htmlFor="logo-upload" style={{ color: '#E8E8E8' }}>
                    {logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Button 
                      type="button"
                      onClick={() => document.getElementById('logo-upload').click()}
                      disabled={uploading}
                      style={{ background: '#FFD700', color: '#0A1128' }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Choose File'}
                    </Button>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>PNG, JPG, or GIF (recommended size: 200x200px)</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name" style={{ color: '#E8E8E8' }}>Company Name *</Label>
                <Input 
                  id="company_name" 
                  data-testid="company-name"
                  value={formData.company_name} 
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} 
                  required 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
              <div>
                <Label htmlFor="website" style={{ color: '#E8E8E8' }}>Website</Label>
                <Input 
                  id="website" 
                  data-testid="company-website"
                  value={formData.website} 
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })} 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address" style={{ color: '#E8E8E8' }}>Address *</Label>
              <Input 
                id="address" 
                data-testid="company-address"
                value={formData.address} 
                onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                required 
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal_code" style={{ color: '#E8E8E8' }}>Postal Code *</Label>
                <Input 
                  id="postal_code" 
                  data-testid="company-postal"
                  value={formData.postal_code} 
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} 
                  required 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
              <div>
                <Label htmlFor="city" style={{ color: '#E8E8E8' }}>City *</Label>
                <Input 
                  id="city" 
                  data-testid="company-city"
                  value={formData.city} 
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })} 
                  required 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vat_number" style={{ color: '#E8E8E8' }}>VAT Number *</Label>
                <Input 
                  id="vat_number" 
                  data-testid="company-vat"
                  value={formData.vat_number} 
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })} 
                  required 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
              <div>
                <Label htmlFor="email" style={{ color: '#E8E8E8' }}>Email *</Label>
                <Input 
                  id="email" 
                  data-testid="company-email"
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                  required 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" style={{ color: '#E8E8E8' }}>Phone</Label>
              <Input 
                id="phone" 
                data-testid="company-phone"
                value={formData.phone} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
              />
            </div>

            <div className="pt-4" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFD700' }}>Bank Details</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="bank_name" style={{ color: '#E8E8E8' }}>Bank Name</Label>
                  <Input 
                    id="bank_name" 
                    data-testid="company-bank-name"
                    placeholder="Bank of Brussels"
                    value={formData.bank_name} 
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div>
                  <Label htmlFor="bank_account" style={{ color: '#E8E8E8' }}>Account Number</Label>
                  <Input 
                    id="bank_account" 
                    data-testid="company-bank-account"
                    placeholder="Account Number"
                    value={formData.bank_account} 
                    onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div>
                  <Label htmlFor="iban" style={{ color: '#E8E8E8' }}>IBAN</Label>
                  <Input 
                    id="iban" 
                    data-testid="company-iban"
                    placeholder="BE68539007547034"
                    value={formData.iban} 
                    onChange={(e) => setFormData({ ...formData, iban: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div>
                  <Label htmlFor="swift_bic" style={{ color: '#E8E8E8' }}>SWIFT/BIC</Label>
                  <Input 
                    id="swift_bic" 
                    data-testid="company-swift"
                    placeholder="GEBABEBB"
                    value={formData.swift_bic} 
                    onChange={(e) => setFormData({ ...formData, swift_bic: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
              </div>
            </div>

            {/* Invoice PDF Information Section */}
            <div className="pt-4" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#FFD700' }}>
                <FileSignature className="h-5 w-5" />
                Invoice PDF Information
              </h3>
              <p className="text-sm mb-4" style={{ color: '#B0B0B0' }}>
                Customize the company information that appears in generated invoice PDFs. Leave fields empty to use the default company settings above.
              </p>
              
              {/* Header Section */}
              <div className="mb-4 p-3 rounded" style={{ background: 'rgba(74, 90, 138, 0.2)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
                <h4 className="font-semibold mb-3" style={{ color: '#E8E8E8' }}>📄 Invoice Header</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="invoice_company_name" style={{ color: '#E8E8E8' }}>Company Name</Label>
                    <Input 
                      id="invoice_company_name" 
                      data-testid="invoice-company-name"
                      placeholder="Air Transport Trade srl"
                      value={formData.invoice_company_name} 
                      onChange={(e) => setFormData({ ...formData, invoice_company_name: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_website" style={{ color: '#E8E8E8' }}>Website</Label>
                    <Input 
                      id="invoice_website" 
                      data-testid="invoice-website"
                      placeholder="www.viphandling.com"
                      value={formData.invoice_website} 
                      onChange={(e) => setFormData({ ...formData, invoice_website: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label htmlFor="invoice_address" style={{ color: '#E8E8E8' }}>Address</Label>
                    <Input 
                      id="invoice_address" 
                      data-testid="invoice-address"
                      placeholder="Rue Franz Merjay 58"
                      value={formData.invoice_address} 
                      onChange={(e) => setFormData({ ...formData, invoice_address: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_city" style={{ color: '#E8E8E8' }}>Postal Code / City / Country</Label>
                    <Input 
                      id="invoice_city" 
                      data-testid="invoice-city"
                      placeholder="1050 Brussels / Ixelles / Belgium"
                      value={formData.invoice_city} 
                      onChange={(e) => setFormData({ ...formData, invoice_city: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label htmlFor="invoice_vat" style={{ color: '#E8E8E8' }}>VAT Number</Label>
                    <Input 
                      id="invoice_vat" 
                      data-testid="invoice-vat"
                      placeholder="BE0811.381.541"
                      value={formData.invoice_vat} 
                      onChange={(e) => setFormData({ ...formData, invoice_vat: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_email" style={{ color: '#E8E8E8' }}>E-mail</Label>
                    <Input 
                      id="invoice_email" 
                      data-testid="invoice-email"
                      placeholder="accounting@viphandling.com"
                      value={formData.invoice_email} 
                      onChange={(e) => setFormData({ ...formData, invoice_email: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label htmlFor="invoice_phone" style={{ color: '#E8E8E8' }}>Phone Number</Label>
                    <Input 
                      id="invoice_phone" 
                      data-testid="invoice-phone"
                      placeholder="+32 472 49 99 47"
                      value={formData.invoice_phone} 
                      onChange={(e) => setFormData({ ...formData, invoice_phone: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_dispatch_label" style={{ color: '#E8E8E8' }}>Dispatch Label</Label>
                    <Input 
                      id="invoice_dispatch_label" 
                      data-testid="invoice-dispatch-label"
                      placeholder="24/7 Dispatch"
                      value={formData.invoice_dispatch_label} 
                      onChange={(e) => setFormData({ ...formData, invoice_dispatch_label: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                </div>
              </div>
              
              {/* Footer Section */}
              <div className="p-3 rounded" style={{ background: 'rgba(74, 90, 138, 0.2)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
                <h4 className="font-semibold mb-3" style={{ color: '#E8E8E8' }}>📋 Invoice Footer (3 lines at bottom of PDF)</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="invoice_footer_line1" style={{ color: '#E8E8E8' }}>Footer Line 1 (Company Name & Address)</Label>
                    <Input 
                      id="invoice_footer_line1" 
                      data-testid="invoice-footer-line1"
                      placeholder='Air Transport Trade srl "A.T.T Jets - and Airport Services" Rue Franz Merjay 58 1050 Brussels - Ixelles - Belgium'
                      value={formData.invoice_footer_line1} 
                      onChange={(e) => setFormData({ ...formData, invoice_footer_line1: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_footer_line2" style={{ color: '#E8E8E8' }}>Footer Line 2 (VAT, Email, Website, Phone)</Label>
                    <Input 
                      id="invoice_footer_line2" 
                      data-testid="invoice-footer-line2"
                      placeholder="BE0811.381.541 - E-mail: accounting@viphandling.com - www.viphandling.com - Tel: +32 472 49 99 47 - 24/7 Dispatch"
                      value={formData.invoice_footer_line2} 
                      onChange={(e) => setFormData({ ...formData, invoice_footer_line2: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_footer_line3" style={{ color: '#E8E8E8' }}>Footer Line 3 (Bank Details)</Label>
                    <Input 
                      id="invoice_footer_line3" 
                      data-testid="invoice-footer-line3"
                      placeholder="Bank: ING - IBAN BE65 3631 2885 0796 - BIC/SWIFT: BBRUBEBB"
                      value={formData.invoice_footer_line3} 
                      onChange={(e) => setFormData({ ...formData, invoice_footer_line3: e.target.value })} 
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                </div>
                <p className="text-xs mt-2" style={{ color: '#B0B0B0' }}>
                  These 3 lines appear at the bottom of every invoice PDF. Leave empty to use automatically generated footer from Bank Details above.
                </p>
              </div>
            </div>

            {/* General Conditions PDF Section */}
            <div className="pt-4" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFD700' }}>General Conditions PDF</h3>
              <div>
                <Label htmlFor="general_conditions_pdf" style={{ color: '#E8E8E8' }}>
                  Upload General Conditions PDF (will be appended to invoices)
                </Label>
                
                {formData.general_conditions_pdf ? (
                  <div style={{ 
                    background: '#0A1128', 
                    border: '1px solid #4A5A8A', 
                    borderRadius: '4px', 
                    padding: '12px',
                    marginTop: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ color: '#FFD700', fontSize: '14px', marginBottom: '4px' }}>✓ PDF Uploaded</p>
                        <p style={{ color: '#B0B0B0', fontSize: '12px' }}>
                          {formData.general_conditions_pdf.split('/').pop()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => window.open(`${BACKEND_URL}${formData.general_conditions_pdf}`, '_blank')}
                          style={{
                            background: '#4A5A8A',
                            color: '#FFFFFF',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          View PDF
                        </button>
                        <button
                          onClick={handleDeleteGeneralConditionsPDF}
                          style={{
                            background: '#8B0000',
                            color: '#FFFFFF',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '8px' }}>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleGeneralConditionsPDFUpload}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px',
                        background: '#0A1128',
                        border: '1px solid #4A5A8A',
                        borderRadius: '4px',
                        color: '#E8E8E8',
                        cursor: 'pointer'
                      }}
                    />
                    <p style={{ color: '#B0B0B0', fontSize: '12px', marginTop: '4px' }}>
                      Upload a PDF file that will be automatically appended to all invoices
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Price List PDF Section */}
            <div className="pt-4" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#FFD700' }}>
                <FileText className="h-5 w-5" />
                Price List PDF
              </h3>
              <div>
                <Label htmlFor="price_list_pdf" style={{ color: '#E8E8E8' }}>
                  Upload Price List PDF (can be sent to agencies)
                </Label>
                
                {(formData.price_list_pdf && !pdfDeleted) ? (
                  <div style={{ 
                    background: '#0A1128', 
                    border: '1px solid #4A5A8A', 
                    borderRadius: '4px', 
                    padding: '12px',
                    marginTop: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ color: '#FFD700', fontSize: '14px', marginBottom: '4px' }}>✓ PDF Uploaded</p>
                        <p style={{ color: '#B0B0B0', fontSize: '12px' }}>
                          {formData.price_list_pdf.split('/').pop()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => window.open(`${BACKEND_URL}${formData.price_list_pdf}`, '_blank')}
                          style={{
                            background: '#4A5A8A',
                            color: '#FFFFFF',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          View PDF
                        </button>
                        <button
                          onClick={handleSendPriceList}
                          style={{
                            background: '#22C55E',
                            color: '#FFFFFF',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Send className="h-3 w-3" />
                          Send
                        </button>
                        <button
                          onClick={handleShowHistory}
                          style={{
                            background: '#FFD700',
                            color: '#000000',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Clock className="h-3 w-3" />
                          History
                        </button>
                        <button
                          onClick={handleDeletePriceListPDF}
                          style={{
                            background: '#8B0000',
                            color: '#FFFFFF',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '8px' }}>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handlePriceListPDFUpload}
                      disabled={uploading}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px',
                        background: '#0A1128',
                        border: '1px solid #4A5A8A',
                        borderRadius: '4px',
                        color: '#E8E8E8',
                        cursor: uploading ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <p style={{ color: '#B0B0B0', fontSize: '12px', marginTop: '4px' }}>
                      {uploading ? 'Uploading...' : 'Upload a PDF file containing your price list to send to agencies'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Crypto Payment Settings Section */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-4" style={{ color: '#FFD700' }}>
                ₿ Cryptocurrency Payment Settings
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="usdc_wallet_address" style={{ color: '#E8E8E8' }}>USDC Wallet Address (Ethereum/ERC-20)</Label>
                  <Input 
                    id="usdc_wallet_address" 
                    placeholder="0x..."
                    value={formData.usdc_wallet_address || ''} 
                    onChange={(e) => setFormData({ ...formData, usdc_wallet_address: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                    Enter your Ethereum wallet address for receiving USDC payments from invoices
                  </p>
                </div>
              </div>
            </div>

            {/* Timezone Settings Section */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-4" style={{ color: '#FFD700' }}>
                <Clock className="h-5 w-5" />
                Time Zone Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="timezone" style={{ color: '#E8E8E8' }}>Time Zone</Label>
                  <Select 
                    value={formData.timezone} 
                    onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                  >
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A', maxHeight: '300px' }}>
                      <SelectItem value="Europe/Brussels">Europe/Brussels (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (GMT/BST)</SelectItem>
                      <SelectItem value="Europe/Amsterdam">Europe/Amsterdam (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Berlin">Europe/Berlin (CET/CEST)</SelectItem>
                      <SelectItem value="America/New_York">America/New York (EST/EDT)</SelectItem>
                      <SelectItem value="America/Chicago">America/Chicago (CST/CDT)</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los Angeles (PST/PDT)</SelectItem>
                      <SelectItem value="Asia/Dubai">Asia/Dubai (GST)</SelectItem>
                      <SelectItem value="Asia/Singapore">Asia/Singapore (SGT)</SelectItem>
                      <SelectItem value="Asia/Tokyo">Asia/Tokyo (JST)</SelectItem>
                      <SelectItem value="Australia/Sydney">Australia/Sydney (AEDT/AEST)</SelectItem>
                      <SelectItem value="UTC">UTC (Coordinated Universal Time)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                    All times in the system will be displayed in this timezone
                  </p>
                </div>
                <div>
                  <Label htmlFor="use_utc" style={{ color: '#E8E8E8' }}>Display Mode</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      id="use_utc"
                      checked={formData.use_utc}
                      onChange={(e) => setFormData({ ...formData, use_utc: e.target.checked })}
                      className="h-4 w-4"
                      style={{ accentColor: '#FFD700' }}
                    />
                    <Label htmlFor="use_utc" className="font-normal cursor-pointer" style={{ color: '#E8E8E8' }}>
                      Display times in UTC instead of local timezone
                    </Label>
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                    When enabled, all times will show in UTC regardless of timezone setting
                  </p>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={saving}
              style={{ background: '#FFFFFF', color: '#0A1128' }}
              data-testid="save-settings"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Price List History Dialog */}
      <Dialog open={showHistoryOpen} onOpenChange={setShowHistoryOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A', maxWidth: '700px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Price List Send History</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              View all price list emails sent to agencies
            </DialogDescription>
            {priceListHistory.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleClearPriceListHistory}
                  style={{ background: '#EF4444', color: '#FFFFFF' }}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear History
                </Button>
              </div>
            )}
          </DialogHeader>
          
          <div className="mt-4">
            <div style={{ 
              maxHeight: '500px', 
              overflowY: 'auto',
              border: '1px solid #4A5A8A',
              borderRadius: '4px',
              background: '#0A1128'
            }}>
              {priceListHistory.length === 0 ? (
                <p style={{ color: '#B0B0B0', textAlign: 'center', padding: '40px' }}>
                  No price lists sent yet
                </p>
              ) : (
                priceListHistory.map((record, index) => (
                  <div 
                    key={record.id || index}
                    style={{
                      padding: '16px',
                      borderBottom: index < priceListHistory.length - 1 ? '1px solid rgba(74, 90, 138, 0.3)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div>
                        <p style={{ color: '#FFFFFF', fontWeight: '500', marginBottom: '4px' }}>
                          Sent by: {record.sent_by}
                        </p>
                        <p style={{ color: '#B0B0B0', fontSize: '12px' }}>
                          {new Date(record.sent_at).toLocaleString()}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ color: '#22C55E', fontSize: '14px', fontWeight: '500' }}>
                          ✓ {record.success_count} sent
                        </p>
                        {record.failed_count > 0 && (
                          <p style={{ color: '#EF4444', fontSize: '12px' }}>
                            ✗ {record.failed_count} failed
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {record.sent_to && record.sent_to.length > 0 && (
                      <div style={{ 
                        marginTop: '8px',
                        padding: '8px',
                        background: 'rgba(34, 197, 94, 0.1)',
                        borderRadius: '4px'
                      }}>
                        <p style={{ color: '#22C55E', fontSize: '11px', fontWeight: '500', marginBottom: '4px' }}>
                          Sent to:
                        </p>
                        <div style={{ fontSize: '11px', color: '#B0B0B0' }}>
                          {record.sent_to.map((agency, i) => (
                            <span key={i}>
                              {agency.agency_name}
                              {i < record.sent_to.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {record.failed_agencies && record.failed_agencies.length > 0 && (
                      <div style={{ 
                        marginTop: '8px',
                        padding: '8px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: '4px'
                      }}>
                        <p style={{ color: '#EF4444', fontSize: '11px', fontWeight: '500', marginBottom: '4px' }}>
                          Failed:
                        </p>
                        <div style={{ fontSize: '11px', color: '#B0B0B0' }}>
                          {record.failed_agencies.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Price List Dialog */}
      <Dialog open={sendPriceListOpen} onOpenChange={setSendPriceListOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A', maxWidth: '600px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Send Price List to Agencies</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Select agencies to send the price list PDF via email
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              border: '1px solid #4A5A8A',
              borderRadius: '4px',
              padding: '8px',
              background: '#0A1128'
            }}>
              {agencies.length === 0 ? (
                <p style={{ color: '#B0B0B0', textAlign: 'center', padding: '20px' }}>
                  No agencies found
                </p>
              ) : (
                agencies.filter(a => a.is_active !== false).map((agency) => (
                  <div 
                    key={agency.id} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      padding: '12px',
                      borderBottom: '1px solid rgba(74, 90, 138, 0.3)',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onClick={() => handleToggleAgency(agency.id)}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(74, 90, 138, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Checkbox 
                      checked={selectedAgencies.includes(agency.id)}
                      onCheckedChange={() => handleToggleAgency(agency.id)}
                      style={{ marginRight: '12px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <p style={{ color: '#FFFFFF', fontWeight: '500', marginBottom: '2px' }}>
                        {agency.name}
                      </p>
                      <p style={{ color: '#B0B0B0', fontSize: '12px' }}>
                        {agency.email}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {selectedAgencies.length > 0 && (
              <div style={{ 
                marginTop: '12px',
                padding: '8px 12px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '4px',
                color: '#22C55E',
                fontSize: '14px'
              }}>
                {selectedAgencies.length} {selectedAgencies.length === 1 ? 'agency' : 'agencies'} selected
              </div>
            )}

            <div style={{ 
              marginTop: '16px', 
              padding: '12px',
              background: 'rgba(255, 215, 0, 0.1)',
              border: '1px solid rgba(255, 215, 0, 0.3)',
              borderRadius: '4px'
            }}>
              <p style={{ color: '#FFD700', fontSize: '12px', marginBottom: '4px', fontWeight: '500' }}>
                ℹ️ Email Configuration
              </p>
              <p style={{ color: '#B0B0B0', fontSize: '12px' }}>
                The price list will be sent from: <strong style={{ color: '#E8E8E8' }}>{formData.email || 'company email'}</strong>
              </p>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <Button
                onClick={() => setSendPriceListOpen(false)}
                style={{ 
                  flex: 1,
                  background: '#4A5A8A', 
                  color: '#FFFFFF' 
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendPriceListToAgencies}
                disabled={selectedAgencies.length === 0 || sending}
                style={{ 
                  flex: 1,
                  background: selectedAgencies.length === 0 ? '#666' : '#22C55E', 
                  color: '#FFFFFF',
                  cursor: selectedAgencies.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                <Send className="mr-2 h-4 w-4" />
                {sending ? 'Sending...' : 'Send Price List'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanySettings;
